// Justesse ARITHMÉTIQUE des formules d'une animation (avant la preuve par le
// noyau du moteur de raisonnement, qui viendra ensuite) : une IA se trompe
// surtout dans les calculs. On lit les membres purement NUMÉRIQUES d'une
// égalité (« 36 + 64 », « \sqrt{100} », « \frac{3}{4} ») et on vérifie :
//   - dans une formule : « 36 + 64 = 100 » — tous les membres numériques égaux ;
//   - d'une formule à la suivante (`formula.transform`) : même membre gauche
//     (« BC^2 = 36 + 64 » → « BC^2 = 100 ») ⇒ même valeur à droite.
// Un membre qui contient une lettre ou une commande inconnue n'est pas lu
// (rien n'est affirmé sur lui). « \approx » tolère l'arrondi affiché.

import { evalExpr } from '@jeraisonne/db/shared/expr';

const SPACING = /\\[,;:! ]|\\quad|\\qquad|~/g;
const LEFT_RIGHT = /\\left|\\right/g;

/**
 * LaTeX numérique → expression de l'évaluateur, ou `null` (lettre, commande
 * non numérique…). Gère + − × ÷ · , \frac, \sqrt (et racine n-ième),
 * puissances, parenthèses, virgule décimale « {,} » ou « , ».
 */
export function texToExpr(tex: string): string | null {
  let s = tex.replace(SPACING, ' ').replace(LEFT_RIGHT, '').trim();
  if (!s) return null;
  s = s
    .replace(/\{,\}/g, '.')
    .replace(/(\d),(\d)/g, '$1.$2')
    .replace(/\\times|\\cdot|×|·/g, '*')
    .replace(/\\div|÷/g, '/')
    .replace(/−/g, '-');
  // \frac{a}{b} et \sqrt[n]{x} / \sqrt{x}, de l'intérieur vers l'extérieur
  for (let guard = 0; guard < 40; guard++) {
    const before = s;
    s = s.replace(/\\[dt]?frac\{([^{}]*)\}\{([^{}]*)\}/g, '(($1)/($2))');
    s = s.replace(/\\sqrt\[([^[\]{}]*)\]\{([^{}]*)\}/g, '(($2)^(1/($1)))');
    s = s.replace(/\\sqrt\{([^{}]*)\}/g, 'sqrt($1)');
    s = s.replace(/\^\{([^{}]*)\}/g, '^($1)');
    if (s === before) break;
  }
  s = s.replace(/[{}]/g, (c) => (c === '{' ? '(' : ')'));
  // reste : chiffres, opérateurs, parenthèses, point, et « sqrt » posé ci-dessus
  if (!/^[\d\s+\-*/^().]*$/.test(s.replace(/sqrt/g, ''))) return null;
  if (!/\d/.test(s)) return null;
  return s;
}

/** Valeur d'un membre numérique, ou `null`. */
export function numericValue(tex: string): number | null {
  const expr = texToExpr(tex);
  if (expr === null) return null;
  try {
    const v = evalExpr(expr, {});
    return Number.isFinite(v) ? v : null;
  } catch {
    return null;
  }
}

export interface Side {
  tex: string;
  value: number | null;
  /** relié au membre précédent par « \approx » */
  approx: boolean;
}

// Plusieurs énoncés dans une même formule : « x = 0 \text{ ou } x = 5 »,
// « a = 2 \quad b = 3 », « x = 3 \Rightarrow y = 7 ». Chacun se vérifie seul :
// lus comme une seule chaîne d'égalités, ils donneraient « 0 = 5 », faux.
const STATEMENT_SEPARATOR =
  /\\text\s*\{\s*(?:ou|et|soit|donc|or|and)\s*\}|\\(?:Rightarrow|Longrightarrow|Leftrightarrow|Longleftrightarrow|iff|implies|quad|qquad)(?![a-zA-Z])|;/y;
const EDGE_SPACING = /^(?:\s|\\[,;:!]|\\quad|\\qquad|~)+|(?:\s|\\[,;:!]|\\quad|\\qquad|~)+$/g;

/** Énoncés d'une formule, séparés au premier niveau par « ou », « et », « ; », ⇒, ⇔… */
export function formulaStatements(tex: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let start = 0;
  for (let i = 0; i < tex.length; i++) {
    const c = tex[i];
    if (depth === 0 && (c === '\\' || c === ';')) {
      STATEMENT_SEPARATOR.lastIndex = i;
      const m = STATEMENT_SEPARATOR.exec(tex);
      if (m) {
        out.push(tex.slice(start, i));
        start = i + m[0].length;
        i = start - 1;
        continue;
      }
    }
    if (c === '\\') {
      // une commande (« \; », « \{ », « \frac ») n'est jamais un séparateur
      const name = /^[a-zA-Z]+/.exec(tex.slice(i + 1));
      i += name ? name[0].length : 1;
    } else if (c === '{' || c === '(' || c === '[') depth++;
    else if (c === '}' || c === ')' || c === ']') depth--;
  }
  out.push(tex.slice(start));
  return out.map((t) => t.replace(EDGE_SPACING, '')).filter((t) => t.length > 0);
}

/** Membres d'une (in)égalité au premier niveau : « a = b \approx c ». */
export function equationSides(tex: string): Side[] {
  const out: Side[] = [];
  let depth = 0;
  let start = 0;
  let approx = false;
  const push = (end: number, nextApprox: boolean) => {
    const part = tex.slice(start, end).trim();
    out.push({ tex: part, value: numericValue(part), approx });
    approx = nextApprox;
  };
  for (let i = 0; i < tex.length; i++) {
    const c = tex[i];
    if (c === '{' || c === '(' || c === '[') depth++;
    else if (c === '}' || c === ')' || c === ']') depth--;
    else if (depth === 0 && c === '=') {
      push(i, false);
      start = i + 1;
    } else if (depth === 0 && tex.startsWith('\\approx', i)) {
      push(i, true);
      i += 6;
      start = i + 1;
    } else if (depth === 0 && c === '≈') {
      push(i, true);
      start = i + 1;
    }
  }
  push(tex.length, false);
  return out;
}

/** Nombre de décimales écrites dans un membre (« 3{,}46 » → 2). */
function decimalsOf(tex: string): number {
  const m = /(\d)(?:\{,\}|[.,])(\d+)/.exec(tex);
  return m ? m[2]!.length : 0;
}

/** Puissance de 10 d'une notation scientifique (« 1{,}44 \\times 10^{17} » → 17), sinon 0. */
function powerOfTenOf(tex: string): number {
  const m = /\\times\s*10\s*\^\s*\{?\s*(-?\d+)\s*\}?\s*$/.exec(tex.trim());
  return m ? Number(m[1]) : 0;
}

function close(a: number, b: number, approxTex?: string): boolean {
  if (approxTex !== undefined) {
    // arrondi affiché : demi-unité de la dernière décimale (et un peu de marge),
    // à l'échelle de la puissance de 10 en notation scientifique
    const tol = 0.5 * 10 ** (powerOfTenOf(approxTex) - decimalsOf(approxTex)) * 1.0001 + 1e-9;
    return Math.abs(a - b) <= tol;
  }
  return Math.abs(a - b) <= 1e-9 * Math.max(1, Math.abs(a), Math.abs(b));
}

function fmt(v: number): string {
  return String(Math.round(v * 1e6) / 1e6).replace('.', ',');
}

/** Erreurs de calcul DANS une formule (membres numériques différents). */
export function formulaArithmeticErrors(tex: string): string[] {
  const parts = formulaStatements(tex);
  if (parts.length > 1) return parts.flatMap(formulaArithmeticErrors);
  const sides = equationSides(parts[0] ?? tex);
  const out: string[] = [];
  let last: Side | null = null;
  for (const side of sides) {
    if (side.value !== null && last && last.value !== null) {
      const ok = close(last.value, side.value, side.approx ? side.tex : undefined);
      if (!ok)
        out.push(
          `« ${last.tex} » vaut ${fmt(last.value)}, pas ${side.approx ? 'environ ' : ''}${fmt(side.value)} (« ${side.tex} »)`,
        );
    }
    if (side.value !== null || !last) last = side;
  }
  return out;
}

const norm = (tex: string) => tex.replace(/\s+/g, '');

/**
 * Erreur de calcul d'une formule à la suivante : même premier membre ⇒
 * même valeur pour les derniers membres numériques.
 */
export function transformArithmeticError(fromTex: string, toTex: string): string | null {
  // plusieurs énoncés (« x = 0 ou x - 5 = 0 ») : pas une chaîne de calcul
  if (formulaStatements(fromTex).length > 1 || formulaStatements(toTex).length > 1) return null;
  const a = equationSides(fromTex);
  const b = equationSides(toTex);
  if (a.length < 2 || b.length < 2) return null;
  if (norm(a[0]!.tex) !== norm(b[0]!.tex)) return null;
  const va = a[a.length - 1]!;
  const vb = b[b.length - 1]!;
  if (va.value === null || vb.value === null) return null;
  if (close(va.value, vb.value, vb.approx ? vb.tex : undefined)) return null;
  return `« ${va.tex} » vaut ${fmt(va.value)}, mais la formule suivante donne ${fmt(vb.value)} (« ${vb.tex} »)`;
}
